import {
	LuIcon,
	Menu,
	type MenuClickTriggerProps,
	type MenuRootProps,
	type MenuVariantProps,
	useLayoutContext,
} from "@overmap-ai/blocks"
import { type AssetType, type IssueType, type Stored } from "@overmap-ai/common"
import { selectAssetTypesOfOrganization, selectIssueTypesOfOrganization } from "@overmap-ai/core"
import { useOpenState } from "@overmap-ai/hooks"
import { useNavigate } from "@tanstack/react-router"
import * as turf from "@turf/turf"
import { memo, type ReactElement, useCallback } from "react"

import { useGeometryPicker, useLocationPicker } from "../../contexts/hooks"
import { SLIDE_OUT_IDS } from "../../layouts"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { selectActiveOrganizationId, selectActiveProjectId } from "../../store/selectors"
import { GeoImageUploadDialog } from "../Dialogs/GeoImageUploadDialog"
import { AssetTypeGroup } from "../MenuGroups/AssetTypeGroup"
import { IssueTypeGroup } from "../MenuGroups/IssueTypeGroup"

enum ObjectCreationMenuPages {
	INITIAL = "initial",
	ISSUE_TYPES = "issue_types",
	ASSET_TYPES = "asset_types",
}

interface ObjectCreationMenuProps
	extends Omit<MenuRootProps, "children">, Omit<MenuClickTriggerProps, "children">, MenuVariantProps {
	children: ReactElement
}

export const ObjectCreationMenu = memo<ObjectCreationMenuProps>((props) => {
	const { children, disabled, size, variant, accentColor, radius, ...rest } = props

	const activeProjectId = useAppSelector(selectActiveProjectId)!
	const activeOrganizationId = useAppSelector(selectActiveOrganizationId)!
	const openLocationPicker = useLocationPicker()
	const openGeometryPicker = useGeometryPicker()
	const { setOpen } = useLayoutContext()
	const navigate = useNavigate()
	const [showGeoImageUploadDialog, openGeoImageUploadDialog, closeGeoImageUploadDialog] = useOpenState(false)
	const issueTypes = useShallowEqaulSelector(selectIssueTypesOfOrganization(activeOrganizationId))
	const assetTypes = useShallowEqaulSelector(selectAssetTypesOfOrganization(activeOrganizationId))

	const handleSelectIssueType = useCallback(
		(issueType: Stored<IssueType>) => {
			openLocationPicker((close) => ({
				onConfirm: (coordinates) => {
					close()

					if (!coordinates) return

					const [offlineIssue, _promise] = sdk.issues.add({
						title: "",
						due_date: null,
						description: null,
						assigned_to: null,
						geo_marker: turf.point(coordinates.toArray()).geometry,
						project: activeProjectId,
						issue_type: issueType.uuid,
						status: issueType.default_status!,
						priority: issueType.default_priority!,
					})

					void navigate({
						from: "/$organizationId/$projectId",
						to: "/$organizationId/$projectId",
						search: {
							issue: offlineIssue.uuid,
						},
					}).then(() => {
						setOpen(SLIDE_OUT_IDS.RIGHT, true)
					})
				},
				onCancel: close,
			}))
		},
		[activeProjectId, navigate, openLocationPicker, setOpen],
	)

	const handleSelectAssetType = useCallback(
		(assetType: Stored<AssetType>) => {
			openGeometryPicker((close) => ({
				onSave: (geometry) => {
					close()

					const [offlineAsset, _promise] = sdk.assets.add({
						project: activeProjectId,
						asset_type: assetType.uuid,
						geometry: geometry,
						label: "",
						description: null,
						status: assetType.default_status!,
					})

					void navigate({
						from: "/$organizationId/$projectId",
						to: "/$organizationId/$projectId",
						search: {
							asset: offlineAsset.uuid,
						},
					}).then(() => {
						setOpen(SLIDE_OUT_IDS.RIGHT, true)
					})
				},
				onCancel: close,
			}))
		},
		[activeProjectId, openGeometryPicker, navigate, setOpen],
	)

	return (
		<>
			<Menu.Root {...rest}>
				<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
				<Menu.Content size={size} radius={radius} accentColor={accentColor} variant={variant}>
					<Menu.Scroll>
						<Menu.Pages defaultPage={ObjectCreationMenuPages.INITIAL}>
							<Menu.PageContent page={ObjectCreationMenuPages.INITIAL}>
								<Menu.PageTrigger page={ObjectCreationMenuPages.ISSUE_TYPES}>
									<LuIcon icon={modelIconMapping.issue} />
									Issue
								</Menu.PageTrigger>
								<Menu.PageTrigger page={ObjectCreationMenuPages.ASSET_TYPES}>
									<LuIcon icon={modelIconMapping.assetType} />
									Asset
								</Menu.PageTrigger>
								<Menu.Item onSelect={openGeoImageUploadDialog}>
									<LuIcon icon={modelIconMapping.camera} />
									Images
								</Menu.Item>
							</Menu.PageContent>
							<Menu.PageContent page={ObjectCreationMenuPages.ISSUE_TYPES}>
								<IssueTypeGroup issueTypes={issueTypes} onSelectIssueType={handleSelectIssueType} />
							</Menu.PageContent>
							<Menu.PageContent page={ObjectCreationMenuPages.ASSET_TYPES}>
								<AssetTypeGroup assetTypes={assetTypes} onSelectAssetType={handleSelectAssetType} />
							</Menu.PageContent>
						</Menu.Pages>
					</Menu.Scroll>
				</Menu.Content>
			</Menu.Root>
			<GeoImageUploadDialog open={showGeoImageUploadDialog} onOpenChange={closeGeoImageUploadDialog} size="sm" />
		</>
	)
})
ObjectCreationMenu.displayName = "ObjectCreationMenu"
